import { Banner, Button, UiLink } from '@nexus/ui';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../api/client';
import { NewPasswordFields, newPasswordProblem, usePasswordError } from '../session/password';
import { PublicFrame } from './public-frame';

const TOKEN = /^[A-Za-z0-9_-]{16,128}$/;

/** The token from the fragment of the link (`#token=…`), which never reaches a server or a log. */
function readToken(): string | null {
  const token = new URLSearchParams(window.location.hash.slice(1)).get('token');
  return token && TOKEN.test(token) ? token : null;
}

/**
 * Target of the links by email (ADR-0041): `invite` sets the first password of a new account,
 * `reset` a new one after "Passwort vergessen" or a link from the administration.
 */
export function PasswordLinkPage({ kind }: { kind: 'invite' | 'reset' }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const errorText = usePasswordError();
  const [token] = useState(readToken);
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [touched, setTouched] = useState(false);
  const save = useMutation({
    mutationFn: () => api.post('/auth/password/reset', { token, password }),
    onSuccess: () => navigate({ to: '/login', search: { passwordSet: true } }),
  });

  // The token stays out of the history of the browser.
  useEffect(() => {
    if (window.location.hash) {
      window.history.replaceState(window.history.state, '', window.location.pathname);
    }
  }, []);

  function submit(event: FormEvent) {
    event.preventDefault();
    setTouched(true);
    if (!newPasswordProblem(password, repeat)) save.mutate();
  }

  // An invitation is renewed by the administration, "Passwort vergessen" only helps with a password.
  const failure =
    save.error instanceof ApiError && save.error.message === 'invalid-token' && kind === 'invite'
      ? t('password.link.invite.expired')
      : save.error
        ? errorText(save.error)
        : null;

  return (
    <PublicFrame
      title={t(`password.link.${kind}.title`)}
      description={t(`password.link.${kind}.hint`)}
    >
      {token ? (
        <form
          onSubmit={submit}
          noValidate
          className="mt-6 flex flex-col gap-4 rounded-lg border border-line bg-surface p-4"
        >
          {failure && (
            <Banner tone="danger" role="alert">
              {failure}
            </Banner>
          )}
          <NewPasswordFields
            password={password}
            repeat={repeat}
            onPassword={setPassword}
            onRepeat={setRepeat}
            touched={touched}
          />
          <Button
            type="submit"
            variant="primary"
            className="md:self-end"
            loading={save.isPending || save.isSuccess}
          >
            {t(`password.link.${kind}.submit`)}
          </Button>
        </form>
      ) : (
        <Banner tone="danger" role="alert" className="mt-6">
          {t('password.link.incomplete')}
        </Banner>
      )}
      <p className="mt-4">
        <UiLink href="/login" className="text-brand-text hover:underline">
          {t('password.link.toLogin')}
        </UiLink>
      </p>
    </PublicFrame>
  );
}
